import React, { useEffect, useState } from "react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import "./StaffProfile.css";

const emptyProfile = {
  first_name: "",
  middle_name: "",
  last_name: "",
  phone: "",
  branch_id: "",
  department: "",
  job_title: "",
  address: "",
};

export default function StaffProfile() {
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState(emptyProfile);
  const [account, setAccount] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.getStaffProfile(), api.getBranchOptions()])
      .then(([profileResponse, branchResponse]) => {
        setAccount(profileResponse.data?.account || {});
        setProfile({
          ...emptyProfile,
          ...(profileResponse.data?.profile || {}),
        });
        setBranches(branchResponse.data || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await api.updateStaffProfile(profile);
      const saved = response.data?.profile || profile;
      setProfile({ ...emptyProfile, ...saved });
      const name = [saved.first_name, saved.middle_name, saved.last_name]
        .filter(Boolean)
        .join(" ");
      updateUser({ name });
      setMessage("Profile saved successfully.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="staff-profile-page">
        <div className="staff-profile-card">Loading profile…</div>
      </div>
    );
  const initials =
    [profile.first_name, profile.last_name]
      .filter(Boolean)
      .map((name) => name[0])
      .join("")
      .toUpperCase() || "ST";
  const profileFields = ["first_name", "last_name", "phone", "branch_id", "department", "job_title", "address"];
  const completedFields = profileFields.filter((field) => String(profile[field] || "").trim()).length;
  const completion = Math.round((completedFields / profileFields.length) * 100);
  return (
    <div className="staff-profile-page">
      <div className="staff-profile-hero">
        <div className="staff-avatar">{initials}</div>
        <div className="staff-identity">
          <span>STAFF ACCOUNT</span>
          <h1>
            {[profile.first_name, profile.middle_name, profile.last_name]
              .filter(Boolean)
              .join(" ") || "Complete your profile"}
          </h1>
          <p>{account.email}</p>
        </div>
        <div className="profile-completion">
          <div className="completion-copy"><small>Profile completion</small><b>{completion}%</b></div>
          <div className="completion-track"><i style={{ width: `${completion}%` }} /></div>
        </div>
        <div className="staff-role">
          <small>Your role</small>
          <strong>{account.role_name || account.role_slug || "Staff"}</strong>
          <code>{account.role_slug}</code>
        </div>
      </div>
      {error && <div className="staff-profile-message error">{error}</div>}
      {message && (
        <div className="staff-profile-message success">{message}</div>
      )}
      <form className="staff-profile-card" onSubmit={save}>
        <div className="profile-section-title">
          <span className="section-eyebrow">PERSONAL WORKSPACE</span>
          <h2>My Profile</h2>
          <p>
            Update your staff and workplace information. Your role is controlled
            by the administrator.
          </p>
        </div>
        <section className="profile-form-section">
          <div className="form-section-heading"><i>01</i><div><h3>Personal details</h3><p>Your name and contact information</p></div></div>
          <div className="staff-profile-grid three-columns">
          <label>
            First name *
            <input
              required
              value={profile.first_name}
              onChange={(e) =>
                setProfile({ ...profile, first_name: e.target.value })
              }
            />
          </label>
          <label>
            Middle name
            <input
              value={profile.middle_name}
              onChange={(e) =>
                setProfile({ ...profile, middle_name: e.target.value })
              }
            />
          </label>
          <label>
            Last name *
            <input
              required
              value={profile.last_name}
              onChange={(e) =>
                setProfile({ ...profile, last_name: e.target.value })
              }
            />
          </label>
          <label>
            Phone
            <input type="tel" placeholder="e.g. +255 7xx xxx xxx"
              value={profile.phone}
              onChange={(e) =>
                setProfile({ ...profile, phone: e.target.value })
              }
            />
          </label>
          <label className="wide-field">
            Address
            <input placeholder="Your current address"
              value={profile.address}
              onChange={(e) =>
                setProfile({ ...profile, address: e.target.value })
              }
            />
          </label>
          </div>
        </section>
        <section className="profile-form-section">
          <div className="form-section-heading"><i>02</i><div><h3>Workplace</h3><p>Your role within the organization</p></div></div>
          <div className="staff-profile-grid">
          <label>
            Branch
            <select
              value={profile.branch_id || ""}
              onChange={(e) =>
                setProfile({ ...profile, branch_id: e.target.value })
              }
            >
              <option value="">Select branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.code} — {branch.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Department
            <input
              value={profile.department}
              onChange={(e) =>
                setProfile({ ...profile, department: e.target.value })
              }
            />
          </label>
          <label>
            Job title
            <input placeholder="Your official job title"
              value={profile.job_title}
              onChange={(e) =>
                setProfile({ ...profile, job_title: e.target.value })
              }
            />
          </label>
          </div>
        </section>
        <section className="profile-form-section account-section">
          <div className="form-section-heading"><i>03</i><div><h3>Account</h3><p>System-managed access information</p></div></div>
          <div className="staff-profile-grid">
          <label>
            Email
            <input readOnly value={account.email || ""} />
          </label>
          <label>
            Role
            <input readOnly value={account.role_name || ""} />
          </label>
          </div>
        </section>
        <div className="staff-profile-actions">
          <span>Changes are applied to your staff profile.</span>
          <button disabled={saving}>
            {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}
